import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { McDrawer } from '@maersk-global/mds-components-core';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { PageWrapperComponent } from '@shared/components/page-wrapper/page-wrapper.component';
import { PaginationComponent } from '@shared/components/pagination/pagination.component';
import { SearchWrapperComponent } from '@shared/components/search-wrapper/search-wrapper.component';
import { SidePanelComponent } from '@shared/components/side-panel/side-panel.component';
import { SearchModel } from '@shared/models';
import { StoreService } from '@shared/services/store.service';
import { CarrierConfigurationsTableComponent } from '@carrier-configurations/components/carrier-configurations-table/carrier-configurations-table.component';
import {
  CarrierConfigurationDetailDto,
  CarrierConfigurationListItemDto,
  CarrierConfigurationListResponse,
  CarrierConfigurationMappingDto,
  CarrierConfigurationVersionItemDto,
  CarrierConfiguration,
  CarrierListItemDto,
  CreateCarrierConfigurationRequest,
  ExecuteTestFlowResponse,
  SavePhaseMappingsRequest,
  TrackingIdentityFieldDto,
  TrackingPollTargetEventDto,
  TrackingPollTargetListItemDto,
  CarrierListDataItem,
} from '@carrier-configurations/models/carrier-configuration.model';
import { CarrierConfigurationsService } from '@carrier-configurations/services/carrier-configurations.service';
import { DialogComponent } from '../shared/components/dialog/dialog.component';
import '@maersk-global/mds-components-core-loading-indicator';
import '@maersk-global/mds-components-core-table';
import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

type OnboardingPhase = 'orchestration' | 'auth' | 'label' | 'tracking' | 'pudo';

@Component({
  selector: 'carrier-configurations',
  templateUrl: './carrier-configurations.component.html',
  styleUrl: './carrier-configurations.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ReactiveFormsModule,
    PageWrapperComponent,
    SearchWrapperComponent,
    PaginationComponent,
    SidePanelComponent,
    DialogComponent,
    CarrierConfigurationsTableComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CarrierConfigurationsComponent implements OnInit {
  tableColumns: TableColumn[] = [];
  tableData: CarrierConfigurationListItemDto[] = [];
  selectedConfiguration?: CarrierConfigurationDetailDto;
  currentRow?: CarrierConfigurationListItemDto;
  currentPage = 1;
  pageSize = 25;
  totalCount = 0;
  totalPages = 0;
  initialSearchValue = '';
  getConfigurationsLoading = false;
  /** True while fetching a single integration for the editor (does not affect the main table). */
  editorDetailLoading = false;
  versionCountByCarrier: Record<string, number> = {};
  isSwitchVersionModalOpen = false;
  switchVersionVersions: CarrierConfigurationVersionItemDto[] = [];
  selectedSwitchVersionId?: number;
  switchVersionCarrierCode = '';
  isEditorSidePanelOpen = false;
  isConfirmModalOpen = false;
  confirmModalMode: 'delete' | 'save' | null = null;
  isTestSidePanelOpen = false;
  isEditMode = false;
  isReadOnlyMode = false;
  currentConfigurationId?: number;
  selectedPhase: OnboardingPhase = 'orchestration';
  existingMappings: CarrierConfigurationMappingDto[] = [];
  phaseMappingKeys: Record<OnboardingPhase, string[]> = {
    orchestration: [],
    auth: [],
    label: [],
    tracking: [],
    pudo: [],
  };
  enabledPhases: Record<OnboardingPhase, boolean> = {
    orchestration: true,
    auth: false,
    label: false,
    tracking: false,
    pudo: false,
  };
  carrierSearchTerm = '';
  carrierPickerOptions: CarrierListItemDto[] = [];
  carrierPickerOptionsData: CarrierListDataItem[] = [];
  carrierPickerLoading = false;
  carrierPickerPage = 1;
  carrierPickerTotal = 0;
  readonly carrierPageSize = 25;
  private readonly carrierSearch$ = new Subject<string>();
  private readonly destroyRef = inject(DestroyRef);
  readonly testFlowOptions: { value: string; labelKey: string }[] = [
    { value: 'label-buy', labelKey: 'carrier_integrations_flow_label_buy' },
    {
      value: 'label-cancel',
      labelKey: 'carrier_integrations_flow_label_cancel',
    },
    {
      value: 'tracking-poll',
      labelKey: 'carrier_integrations_flow_tracking_updates',
    },
    { value: 'pudo-poll', labelKey: 'carrier_integrations_flow_pudo_polling' },
    {
      value: 'manifest-create',
      labelKey: 'carrier_integrations_flow_manifest_generation',
    },
  ];
  testFlowName = 'label-buy';
  testCarrierCode = '';
  testVersions: CarrierConfigurationVersionItemDto[] = [];
  selectedTestConfigurationId?: number;
  editorVersions: CarrierConfigurationVersionItemDto[] = [];
  selectedEditorVersionId?: number;
  testRequestJson = '{}';
  pendingSaveAction: (() => void) | null = null;
  phaseDrafts: Record<OnboardingPhase, Record<string, string>> = {
    orchestration: {},
    auth: {},
    label: {},
    tracking: {},
    pudo: {},
  };
  private lastSavedConfigurationJson = '';

  testResponseJson = '';
  testResponseStatus = '';
  testDurationMs?: number;
  testInProgress = false;

  isTrackingSidePanelOpen = false;
  trackingPanelView: 'list' | 'detail' = 'list';
  trackingCarrierCode = '';
  trackingSearchInitialValue = '';
  trackingSearchQuery = '';
  trackingTargets: TrackingPollTargetListItemDto[] = [];
  trackingListColumns: TableColumn[] = [];
  trackingEventColumns: TableColumn[] = [];
  trackingEvents: TrackingPollTargetEventDto[] = [];
  trackingListLoading = false;
  trackingEventsLoading = false;
  trackingCurrentPage = 1;
  readonly trackingPageSize = 10;
  trackingTotalCount = 0;
  trackingTotalPages = 0;
  selectedTrackingTargetId?: number;
  selectedTrackingIdentifier = '';

  listResponse: CarrierConfigurationListResponse = {
    items: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 25,
  };

  editorForm = new FormGroup({
    carrierCode: new FormControl('', { nonNullable: true }),
    isActive: new FormControl('inactive', { nonNullable: true }),
    configurationJson: new FormControl('', { nonNullable: true }),
  });

  mappingForm = new FormGroup({
    mappingKey: new FormControl('', { nonNullable: true }),
    mappingJson: new FormControl('{}', { nonNullable: true }),
  });

  readonly saveDescriptionMaxLength = 200;
  saveDescriptionControl = new FormControl('', { nonNullable: true });

  i18nService = inject(TranslocoService);
  storeService = inject(StoreService);
  private readonly service = inject(CarrierConfigurationsService);

  ngOnInit(): void {
    this.tableColumns = this.service.getTableColumns();
    this.loadConfigurations();
    this.i18nService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.initTrackingTableColumns());

    this.carrierSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.carrierPickerLoading = true;
        }),
        switchMap((term) =>
          this.service.listCarriers(term || undefined, 1, this.carrierPageSize),
        ),
        tap(() => {
          this.carrierPickerLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.carrierPickerOptions = res.items;
          this.carrierPickerOptionsData = res.items.map((option) => ({
            label: option.code,
            value: option.code,
            sublabel: option.name,
          }));
          this.carrierPickerPage = 1;
          this.carrierPickerTotal = res.totalCount;
        },
        error: () => {
          this.carrierPickerLoading = false;
          this.carrierPickerOptions = [];
          this.carrierPickerOptionsData = [];
        },
      });
  }

  getSearchModelByType(): SearchModel {
    return {
      placeholder: this.i18nService.translate('search_by_carrier'),
      searchedTearm: this.onSearch.bind(this),
    };
  }

  onSearch(value: string) {
    this.currentPage = 1;
    this.initialSearchValue = value;
    this.loadConfigurations();
  }

  onTopRightHeaderItemClick(_: Event) {
    this.openCreateDrawer();
  }

  openCreateDrawer() {
    this.editorDetailLoading = false;
    this.isEditMode = false;
    this.isReadOnlyMode = false;
    this.currentRow = undefined;
    this.selectedConfiguration = undefined;
    this.currentConfigurationId = undefined;
    this.selectedEditorVersionId = undefined;
    this.editorVersions = [];
    this.selectedPhase = 'orchestration';
    this.existingMappings = [];
    this.resetPhaseDrafts();
    this.editorForm.controls.carrierCode.setValue('');
    this.editorForm.controls.isActive.setValue('inactive');
    this.editorForm.controls.configurationJson.setValue('{}');
    this.lastSavedConfigurationJson =
      this.editorForm.controls.configurationJson.value;
    this.mappingForm.controls.mappingKey.setValue('');
    this.mappingForm.controls.mappingJson.setValue('{}');
    this.recalculateEnabledPhases();
    this.resetCarrierPicker();
    this.loadInitialCarrierPage();
    this.toggleEditorDrawer(true);
    this.storeService.showSidePanelNotification = false;
  }

  onEditClicked(row: CarrierConfigurationListItemDto) {
    this.openConfigurationDrawer(row, false);
  }

  onDeleteClicked(row: CarrierConfigurationListItemDto) {
    this.currentRow = row;
    this.confirmModalMode = 'delete';
    this.toggleConfirmModal(true);
  }

  onTestClicked(row: CarrierConfigurationListItemDto) {
    this.openStandaloneTestPanel(row.carrierCode, row.id);
  }

  onTrackingClicked(row: CarrierConfigurationListItemDto) {
    this.trackingCarrierCode = row.carrierCode ?? '';
    this.trackingPanelView = 'list';
    this.trackingCurrentPage = 1;
    this.trackingSearchInitialValue = '';
    this.trackingSearchQuery = '';
    this.selectedTrackingTargetId = undefined;
    this.selectedTrackingIdentifier = '';
    this.trackingEvents = [];
    this.toggleTrackingDrawer(true);
    this.loadTrackingTargets();
  }

  get trackingSidePanelTitle(): string {
    if (this.trackingPanelView === 'detail') {
      const base = this.i18nService.translate(
        'carrier_integrations_tracking_events_title',
      );
      return this.selectedTrackingIdentifier
        ? `${base} — ${this.selectedTrackingIdentifier}`
        : base;
    }
    const prefix = this.i18nService.translate('carrier_integrations_tracking');
    return this.trackingCarrierCode
      ? `${prefix} — ${this.trackingCarrierCode}`
      : prefix;
  }

  getTrackingSearchModel(): SearchModel {
    return {
      placeholder: this.i18nService.translate(
        'carrier_integrations_tracking_search_placeholder',
      ),
      searchedTearm: this.onTrackingSearch.bind(this),
    };
  }

  onTrackingSearch(value: string) {
    this.trackingSearchQuery = value ?? '';
    this.trackingCurrentPage = 1;
    this.loadTrackingTargets();
  }

  onTrackingPageChanged(event: Event) {
    const detail = (event as CustomEvent).detail;
    if (detail != null && this.trackingCurrentPage !== detail) {
      this.trackingCurrentPage = detail;
      this.loadTrackingTargets();
    }
  }

  onInvestigateTracking(event: Event, item: TrackingPollTargetListItemDto) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedTrackingTargetId = item.id;
    this.selectedTrackingIdentifier = item.primaryIdentifier;
    this.trackingPanelView = 'detail';
    this.loadTrackingEvents();
  }

  onTrackingBack(event: Event) {
    event.preventDefault();
    this.trackingPanelView = 'list';
    this.selectedTrackingTargetId = undefined;
    this.selectedTrackingIdentifier = '';
    this.trackingEvents = [];
  }

  onTrackingPanelClose(event: Event) {
    event.preventDefault();
    this.toggleTrackingDrawer(false);
    this.trackingPanelView = 'list';
    this.trackingTargets = [];
    this.trackingEvents = [];
  }

  formatIdentityFields(
    fields: TrackingIdentityFieldDto[] | null | undefined,
  ): string {
    if (!fields || fields.length === 0) {
      return '';
    }

    return fields.map((field) => `${field.label}: ${field.value}`).join(', ');
  }

  formatTrackingUtc(iso: string | null | undefined): string {
    if (!iso) {
      return '';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
  }

  onActivateClicked(row: CarrierConfigurationListItemDto) {
    this.service.activate(row.id).subscribe({
      next: () => {
        this.storeService.triggerNotification(
          this.i18nService.translate('carrier_config_activate_success'),
          'success',
          'check-circle',
        );
        this.loadConfigurations();
      },
      error: (err) => {
        const message = this.extractErrorMessage(err);
        this.storeService.triggerNotification(
          message ??
            this.i18nService.translate('carrier_config_activate_error'),
          'error',
          'exclamation-octagon',
        );
      },
    });
  }

  onSwitchActiveVersionClicked(row: CarrierConfigurationListItemDto) {
    this.switchVersionCarrierCode = row.carrierCode;
    this.selectedSwitchVersionId = undefined;
    this.service.listVersionsByCarrier(row.carrierCode).subscribe({
      next: (items) => {
        this.switchVersionVersions = items;
        this.selectedSwitchVersionId =
          items.find((x) => x.id === row.id)?.id ?? items[0]?.id;
        this.isSwitchVersionModalOpen = true;
      },
      error: () => {
        this.storeService.triggerNotification(
          this.i18nService.translate('carrier_config_load_error'),
          'error',
          'exclamation-octagon',
        );
      },
    });
  }

  onSwitchVersionSelect(event: Event) {
    const value = Number((event as CustomEvent).detail?.value ?? 0);
    if (Number.isFinite(value) && value > 0) {
      this.selectedSwitchVersionId = value;
    }
  }

  onSwitchVersionConfirm(event: Event) {
    event.preventDefault();
    if (!this.selectedSwitchVersionId) {
      return;
    }
    this.service.activate(this.selectedSwitchVersionId).subscribe({
      next: () => {
        this.isSwitchVersionModalOpen = false;
        this.storeService.triggerNotification(
          this.i18nService.translate('carrier_config_activate_success'),
          'success',
          'check-circle',
        );
        this.loadConfigurations();
      },
      error: (err) => {
        const message = this.extractErrorMessage(err);
        this.storeService.triggerNotification(
          message ??
            this.i18nService.translate('carrier_config_activate_error'),
          'error',
          'exclamation-octagon',
        );
      },
    });
  }

  onSwitchVersionCancel() {
    this.isSwitchVersionModalOpen = false;
  }

  onSaveConfiguration(event: Event) {
    event.preventDefault();
    this.storeService.showSidePanelNotification = false;
    if (this.isReadOnlyMode) {
      return;
    }

    if (this.selectedPhase !== 'orchestration') {
      this.requestSaveConfirmation(() => this.savePhaseMapping());
      return;
    }

    const carrierCode = this.editorForm.controls.carrierCode.value.trim();
    const isActive = this.editorForm.controls.isActive.value === 'active';
    const configurationJson = this.editorForm.controls.configurationJson.value;

    if (!carrierCode || !configurationJson) {
      this.storeService.triggerSidePanelNotification(
        this.i18nService.translate('carrier_config_validation_error'),
        '',
        'error',
        'exclamation-octagon',
      );
      return;
    }

    try {
      const parsedConfiguration = JSON.parse(configurationJson);
      const shouldActivate = isActive;
      if (!this.isEditMode) {
        this.requestSaveConfirmation(() => {
          const description = this.getSaveDescription();
          if (!description) {
            return;
          }

          const payload: CreateCarrierConfigurationRequest = {
            carrierCode,
            description,
            configuration: parsedConfiguration,
            isActive: false,
          };

          this.service.create(payload).subscribe({
            next: (created) => {
              this.currentConfigurationId = created.id;
              this.currentRow = {
                id: created.id,
                carrierCode: created.carrierCode,
                activeVersion: null,
                latestVersion: created.version,
                updatedAtUtc: created.createdAtUtc,
              };
              this.isEditMode = true;
              this.selectedPhase = 'auth';
              this.lastSavedConfigurationJson =
                this.editorForm.controls.configurationJson.value;
              this.recalculateEnabledPhases();
              this.loadConfigurations();
              this.loadEditorVersions(created.carrierCode, created.id);
              this.storeService.triggerNotification(
                this.i18nService.translate('carrier_config_create_success'),
                'success',
                'check-circle',
              );
              this.loadMappings(created.id);
              if (shouldActivate) {
                this.activateCurrentVersion(created.id);
              }
            },
            error: () => {
              this.storeService.triggerSidePanelNotification(
                this.i18nService.translate('carrier_config_create_error'),
                '',
                'error',
                'exclamation-octagon',
              );
            },
          });
        });
        return;
      }

      if (!this.currentRow) {
        return;
      }

      this.requestSaveConfirmation(() => {
        const description = this.getSaveDescription();
        if (!description) {
          return;
        }

        this.service
          .update(this.currentRow!.id, {
            description,
            configuration: parsedConfiguration,
            isActive: false,
          })
          .subscribe({
            next: (updated) => {
              this.currentConfigurationId = updated.id;
              this.selectedEditorVersionId = updated.id;
              this.currentRow = {
                id: updated.id,
                carrierCode: updated.carrierCode,
                activeVersion: null,
                latestVersion: updated.version,
                updatedAtUtc: updated.createdAtUtc,
              };
              this.editorForm.controls.configurationJson.setValue(
                JSON.stringify(updated.configuration, null, 2),
              );
              this.lastSavedConfigurationJson =
                this.editorForm.controls.configurationJson.value;
              this.recalculateEnabledPhases();
              this.loadConfigurations();
              this.loadEditorVersions(updated.carrierCode, updated.id);
              this.loadMappings(updated.id);
              this.storeService.triggerNotification(
                this.i18nService.translate('carrier_config_update_success'),
                'success',
                'check-circle',
              );
            },
            error: () => {
              this.storeService.triggerSidePanelNotification(
                this.i18nService.translate('carrier_config_update_error'),
                '',
                'error',
                'exclamation-octagon',
              );
            },
          });
      });
    } catch {
      this.storeService.triggerSidePanelNotification(
        this.i18nService.translate('carrier_config_invalid_json'),
        'configurationJson',
        'error',
        'exclamation-octagon',
      );
    }
  }

  onConfirmModalPrimary(event: Event) {
    event.preventDefault();
    if (this.confirmModalMode === 'save') {
      if (!this.isSaveDescriptionValid()) {
        return;
      }

      const action = this.pendingSaveAction;
      this.pendingSaveAction = null;
      this.toggleConfirmModal(false);
      action?.();
      this.saveDescriptionControl.setValue('');
      return;
    }

    if (this.confirmModalMode !== 'delete' || !this.currentRow) {
      return;
    }

    this.service.delete(this.currentRow.id).subscribe({
      next: () => {
        this.toggleConfirmModal(false);
        this.storeService.triggerNotification(
          this.i18nService.translate('carrier_config_delete_success'),
          'success',
          'check-circle',
        );
        this.loadConfigurations();
      },
      error: () => {
        this.storeService.triggerNotification(
          this.i18nService.translate('carrier_config_delete_error'),
          'error',
          'exclamation-octagon',
        );
      },
    });
  }

  onConfirmModalSecondary() {
    this.pendingSaveAction = null;
    this.toggleConfirmModal(false);
    this.saveDescriptionControl.setValue('');
  }

  onEditorCancel(event: Event) {
    event.preventDefault();
    this.toggleTestDrawer(false);
    this.closeEditorDrawer();
  }

  onCarrierSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.carrierSearchTerm = value;
    this.carrierSearch$.next(value);
  }

  loadMoreCarriers() {
    if (this.carrierPickerOptions.length >= this.carrierPickerTotal) {
      return;
    }

    const nextPage = this.carrierPickerPage + 1;
    this.carrierPickerLoading = true;
    this.service
      .listCarriers(
        this.carrierSearchTerm || undefined,
        nextPage,
        this.carrierPageSize,
      )
      .subscribe({
        next: (res) => {
          this.carrierPickerLoading = false;
          this.carrierPickerPage = nextPage;
          this.carrierPickerOptions = [
            ...this.carrierPickerOptions,
            ...res.items,
          ];
          this.carrierPickerTotal = res.totalCount;
        },
        error: () => {
          this.carrierPickerLoading = false;
        },
      });
  }

  onCarrierSelected(carrier: Event) {
    const selectedCarrier = (carrier as CustomEvent)
      .detail as CarrierListDataItem;
    this.editorForm.controls.carrierCode.setValue(selectedCarrier.value);
  }

  onCarrierDeselected() {
    this.editorForm.controls.carrierCode.setValue('');
  }

  onConfigurationJsonInput(event: Event) {
    if (this.isReadOnlyMode) {
      return;
    }

    const value = (event.target as HTMLTextAreaElement)?.value ?? '';
    this.editorForm.controls.configurationJson.setValue(value);
    this.recalculateEnabledPhases();
  }

  onStatusSelect(event: Event) {
    const value = (event as CustomEvent).detail?.value ?? 'inactive';
    this.editorForm.controls.isActive.setValue(value);
  }

  onPhaseSelect(event: Event) {
    const value = (event as CustomEvent).detail?.value as
      | OnboardingPhase
      | undefined;
    if (!value) {
      return;
    }

    if (!this.isPhaseEnabled(value)) {
      return;
    }

    this.persistCurrentMappingDraft();
    this.selectedPhase = value;
    this.mappingForm.controls.mappingJson.setValue('{}');
    const mappingKeys = this.getCurrentPhaseMappingKeys();
    this.mappingForm.controls.mappingKey.setValue(mappingKeys[0] ?? '');
    this.populateMappingBodyFromLoadedMappings();
  }

  onMappingKeySelect(event: Event) {
    const value = (event as CustomEvent).detail?.value as string | undefined;
    if (!value) {
      return;
    }

    this.persistCurrentMappingDraft();
    this.mappingForm.controls.mappingKey.setValue(value);
    this.populateMappingBodyFromLoadedMappings();
  }

  onMappingJsonInput(event: Event) {
    if (this.isReadOnlyMode) {
      return;
    }

    const value = (event.target as HTMLTextAreaElement)?.value ?? '{}';
    this.mappingForm.controls.mappingJson.setValue(value);
    this.persistCurrentMappingDraft();
  }

  onTestFlowSelect(event: Event) {
    const value = (event as CustomEvent).detail?.value as string | undefined;
    if (value && this.testFlowOptions.some((o) => o.value === value)) {
      this.testFlowName = value;
    }
  }

  onTestRequestInput(event: Event) {
    const value = (event.target as HTMLTextAreaElement)?.value ?? '{}';
    this.testRequestJson = value;
  }

  onRunIntegrationTest(event: Event) {
    event.preventDefault();
    if (!this.selectedTestConfigurationId || this.testInProgress) {
      return;
    }

    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(this.testRequestJson) as Record<string, unknown>;
    } catch {
      this.testResponseStatus = 'ERROR';
      this.testResponseJson =
        'REQUEST_JSON_INVALID: Test payload must be valid JSON.';
      this.testDurationMs = undefined;
      return;
    }

    this.testInProgress = true;
    this.service
      .executeTestFlow({
        configurationId: this.selectedTestConfigurationId,
        flowName: this.testFlowName,
        payload,
      })
      .subscribe({
        next: (response: ExecuteTestFlowResponse) => {
          this.testInProgress = false;
          this.testResponseStatus = response.status;
          this.testDurationMs = response.durationMs;
          this.testResponseJson = JSON.stringify(response.result, null, 2);
        },
        error: (err) => {
          this.testInProgress = false;
          this.testResponseStatus = 'ERROR';
          this.testDurationMs = undefined;
          const message =
            this.extractErrorMessage(err) ?? 'TEST_EXECUTION_FAILED';
          this.testResponseJson = message;
        },
      });
  }

  closeTestPanel(event: Event) {
    event.preventDefault();
    this.toggleTestDrawer(false);
  }

  onTestVersionSelect(event: Event) {
    const value = Number((event as CustomEvent).detail?.value ?? 0);
    if (Number.isFinite(value) && value > 0) {
      this.selectedTestConfigurationId = value;
    }
  }

  onEditorVersionSelect(event: Event) {
    const value = Number((event as CustomEvent).detail?.value ?? 0);
    if (
      !Number.isFinite(value) ||
      value <= 0 ||
      value === this.currentConfigurationId
    ) {
      return;
    }

    this.loadConfigurationById(value, this.isReadOnlyMode);
  }

  pageChanged(event: Event) {
    const currentEvent = event as CustomEvent;
    if (currentEvent && this.currentPage !== currentEvent.detail) {
      this.currentPage = currentEvent.detail;
      this.loadConfigurations();
    }
  }

  onRowClicked(row: CarrierConfigurationListItemDto) {
    this.openConfigurationDrawer(row, true);
  }

  private toggleEditorDrawer(open: boolean) {
    const drawer: McDrawer | null = document.body.querySelector(
      '.carrier-config-editor-side-panel',
    );

    if (drawer) {
      drawer.open = open;
      this.isEditorSidePanelOpen = open;
    }
  }

  private toggleConfirmModal(open: boolean) {
    this.isConfirmModalOpen = open;
  }

  private toggleTestDrawer(open: boolean) {
    const drawer: McDrawer | null = document.body.querySelector(
      '.carrier-config-test-side-panel',
    );

    if (drawer) {
      drawer.open = open;
      this.isTestSidePanelOpen = open;
    }
  }

  private toggleTrackingDrawer(open: boolean) {
    const drawer: McDrawer | null = document.body.querySelector(
      '.carrier-tracking-side-panel',
    );

    if (drawer) {
      drawer.open = open;
      this.isTrackingSidePanelOpen = open;
    }
  }

  private initTrackingTableColumns(): void {
    this.trackingListColumns = [
      {
        id: 'identityFields',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_identifiers',
        ),
        sortDisabled: true,
        width: '40%',
      },
      {
        id: 'createdAtUtc',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_created',
        ),
        sortDisabled: true,
        width: '18%',
      },
      {
        id: 'lastPolledUtc',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_last_event',
        ),
        sortDisabled: true,
        width: '18%',
      },
      {
        id: 'flags',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_flags',
        ),
        sortDisabled: true,
        width: '14%',
      },
      {
        id: 'investigate',
        label: '',
        sortDisabled: true,
        rowClickDisabled: true,
        width: '10%',
      },
    ];
    this.trackingEventColumns = [
      {
        id: 'observedAtUtc',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_observed',
        ),
        sortDisabled: true,
        width: '18%',
      },
      {
        id: 'status',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_status',
        ),
        sortDisabled: true,
        width: '12%',
      },
      {
        id: 'description',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_description',
        ),
        sortDisabled: true,
        width: '22%',
      },
      {
        id: 'location',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_location',
        ),
        sortDisabled: true,
        width: '18%',
      },
      {
        id: 'carrierEventUtc',
        label: this.i18nService.translate(
          'carrier_integrations_tracking_carrier_event',
        ),
        sortDisabled: true,
        width: '18%',
      },
    ];
  }

  private loadTrackingTargets(): void {
    if (!this.trackingCarrierCode) {
      return;
    }

    this.trackingListLoading = true;
    const search =
      this.trackingSearchQuery.trim().length > 0
        ? this.trackingSearchQuery.trim()
        : undefined;
    this.service
      .listTrackingPollTargets(
        this.trackingCarrierCode,
        this.trackingCurrentPage,
        this.trackingPageSize,
        search,
      )
      .subscribe({
        next: (res) => {
          this.trackingTargets = res.items;
          this.trackingTotalCount = res.totalCount;
          this.trackingTotalPages =
            res.totalCount === 0
              ? 0
              : Math.ceil(res.totalCount / this.trackingPageSize);
          this.trackingListLoading = false;
        },
        error: () => {
          this.trackingListLoading = false;
          this.storeService.triggerNotification(
            this.i18nService.translate(
              'carrier_integrations_tracking_load_error',
            ),
            'error',
            'exclamation-octagon',
          );
        },
      });
  }

  private loadTrackingEvents(): void {
    if (!this.selectedTrackingTargetId) {
      return;
    }

    this.trackingEventsLoading = true;
    this.service
      .listTrackingPollTargetEvents(this.selectedTrackingTargetId)
      .subscribe({
        next: (rows) => {
          this.trackingEvents = rows;
          this.trackingEventsLoading = false;
        },
        error: () => {
          this.trackingEventsLoading = false;
          this.storeService.triggerNotification(
            this.i18nService.translate(
              'carrier_integrations_tracking_load_error',
            ),
            'error',
            'exclamation-octagon',
          );
        },
      });
  }

  private closeEditorDrawer() {
    this.editorDetailLoading = false;
    this.toggleEditorDrawer(false);
    this.toggleTestDrawer(false);
    this.toggleTrackingDrawer(false);
    this.isEditMode = false;
    this.isReadOnlyMode = false;
    this.currentRow = undefined;
    this.selectedConfiguration = undefined;
    this.currentConfigurationId = undefined;
    this.selectedEditorVersionId = undefined;
    this.editorVersions = [];
    this.selectedPhase = 'orchestration';
    this.existingMappings = [];
    this.resetPhaseDrafts();
    this.resetCarrierPicker();
    this.storeService.showSidePanelNotification = false;
  }

  resetCarrierPicker() {
    this.carrierSearchTerm = '';
    this.carrierPickerOptions = [];
    this.carrierPickerOptionsData = [];
    this.carrierPickerPage = 1;
    this.carrierPickerTotal = 0;
    this.carrierPickerLoading = false;
  }

  private loadInitialCarrierPage() {
    this.carrierPickerLoading = true;
    this.service.listCarriers(undefined, 1, this.carrierPageSize).subscribe({
      next: (res) => {
        this.carrierPickerLoading = false;
        this.carrierPickerOptions = res.items;
        this.carrierPickerOptionsData = res.items.map((option) => ({
          label: option.code,
          value: option.code,
          sublabel: option.name,
        }));
        this.carrierPickerPage = 1;
        this.carrierPickerTotal = res.totalCount;
      },
      error: () => {
        this.carrierPickerLoading = false;
        this.carrierPickerOptions = [];
        this.carrierPickerOptionsData = [];
      },
    });
  }

  private afterSuccessfulSave(successKey: string) {
    this.closeEditorDrawer();
    this.currentPage = 1;
    this.loadConfigurations();
    this.storeService.triggerNotification(
      this.i18nService.translate(successKey),
      'success',
      'check-circle',
    );
  }

  /** Fetches real version count per carrier from API (list is grouped, so one row per carrier). */
  private loadVersionCountByCarrier(
    items: CarrierConfigurationListItemDto[],
  ): void {
    const uniqueCarriers = [
      ...new Set(items.map((i) => i.carrierCode ?? '').filter(Boolean)),
    ];
    if (uniqueCarriers.length === 0) {
      this.versionCountByCarrier = {};
      return;
    }
    forkJoin(
      uniqueCarriers.map((code) =>
        this.service
          .listVersionsByCarrier(code)
          .pipe(map((versions) => ({ code, count: versions.length }))),
      ),
    ).subscribe({
      next: (results) => {
        const count: Record<string, number> = {};
        for (const { code, count: c } of results) {
          count[code] = c;
        }
        this.versionCountByCarrier = count;
      },
      error: () => {
        this.versionCountByCarrier = {};
      },
    });
  }

  private loadConfigurations() {
    this.getConfigurationsLoading = true;
    this.service
      .list(this.currentPage, this.pageSize, this.initialSearchValue, undefined)
      .subscribe({
        next: (response) => {
          this.listResponse = response;
          this.tableData = this.service.mapForTable(response.items);
          this.totalCount = response.totalCount;
          this.totalPages = Math.ceil(response.totalCount / this.pageSize);
          this.getConfigurationsLoading = false;
          this.loadVersionCountByCarrier(response.items);
        },
        error: () => {
          this.getConfigurationsLoading = false;
          this.storeService.triggerNotification(
            this.i18nService.translate('carrier_config_load_error'),
            'error',
            'exclamation-octagon',
          );
        },
      });
  }

  private savePhaseMapping() {
    if (!this.currentConfigurationId) {
      this.storeService.triggerSidePanelNotification(
        this.i18nService.translate(
          'carrier_config_phase_requires_orchestration',
        ),
        '',
        'error',
        'exclamation-octagon',
      );
      return;
    }

    this.persistCurrentMappingDraft();
    const mappings = this.getDirtyPhaseDrafts();
    if (mappings.length === 0) {
      this.storeService.triggerSidePanelNotification(
        this.i18nService.translate('carrier_config_mapping_required'),
        '',
        'error',
        'exclamation-octagon',
      );
      return;
    }

    const configurationPatch = this.getConfigurationPatchForPhaseSave();
    if (configurationPatch === undefined) {
      return;
    }

    const description = this.getSaveDescription();
    if (!description) {
      return;
    }

    const payload: SavePhaseMappingsRequest = {
      phase: this.selectedPhase,
      description,
      mappings,
      configurationPatch: configurationPatch ?? undefined,
    };

    this.service
      .savePhaseMappings(this.currentConfigurationId, payload)
      .subscribe({
        next: (saved) => {
          this.currentConfigurationId = saved.id;
          this.currentRow = {
            id: saved.id,
            carrierCode: saved.carrierCode,
            activeVersion: saved.isActive ? saved.version : null,
            latestVersion: saved.version,
            updatedAtUtc: saved.createdAtUtc,
          };
          this.editorForm.controls.configurationJson.setValue(
            JSON.stringify(saved.configuration, null, 2),
          );
          this.lastSavedConfigurationJson =
            this.editorForm.controls.configurationJson.value;
          this.resetPhaseDraftFor(this.selectedPhase);
          this.storeService.triggerNotification(
            this.i18nService.translate('carrier_config_mapping_saved'),
            'success',
            'check-circle',
          );
          this.recalculateEnabledPhases();
          this.loadConfigurations();
          this.loadMappings(saved.id);
        },
        error: () => {
          this.storeService.triggerSidePanelNotification(
            this.i18nService.translate('carrier_config_mapping_error'),
            '',
            'error',
            'exclamation-octagon',
          );
        },
      });
  }

  private loadMappings(configurationId: number) {
    this.service.listMappings(configurationId).subscribe({
      next: (items) => {
        this.existingMappings = items;
        const mappingKeys = this.getCurrentPhaseMappingKeys();
        if (
          !this.mappingForm.controls.mappingKey.value &&
          mappingKeys.length > 0
        ) {
          this.mappingForm.controls.mappingKey.setValue(mappingKeys[0]);
        }

        this.populateMappingBodyFromLoadedMappings();
      },
      error: () => {
        this.existingMappings = [];
      },
    });
  }

  getCurrentPhaseMappingKeys(): string[] {
    if (this.selectedPhase === 'orchestration') {
      return [];
    }

    return this.phaseMappingKeys[this.selectedPhase] ?? [];
  }

  getPhaseLabel(phase: OnboardingPhase): string {
    const translationKey =
      phase === 'label'
        ? 'carrier_config_phase_name_label'
        : `carrier_config_phase_${phase}`;
    return this.i18nService.translate(translationKey);
  }

  getPhaseOptionLabel(phase: OnboardingPhase): string {
    const label = this.getPhaseLabel(phase);
    if (phase === 'orchestration') {
      return label;
    }

    return this.isPhaseComplete(phase) ? `${label} [x]` : label;
  }

  canEditMappings(): boolean {
    return !!this.currentConfigurationId;
  }

  isPhaseEnabled(phase: OnboardingPhase): boolean {
    return this.enabledPhases[phase];
  }

  getPrimaryActionLabel(): string {
    return this.selectedPhase === 'orchestration'
      ? this.i18nService.translate('save_changes')
      : this.i18nService.translate('carrier_config_save_phase');
  }

  private populateMappingBodyFromLoadedMappings() {
    const key = this.mappingForm.controls.mappingKey.value;
    if (!key) {
      this.mappingForm.controls.mappingJson.setValue('{}');
      return;
    }

    const draft = this.getDraftValue(this.selectedPhase, key);
    if (draft !== null) {
      this.mappingForm.controls.mappingJson.setValue(draft);
      return;
    }

    const existing = this.existingMappings.find(
      (x) => x.mappingKey.toLowerCase() === key.toLowerCase(),
    );
    this.mappingForm.controls.mappingJson.setValue(
      existing?.mappingJson ?? '{}',
    );
  }

  private recalculateEnabledPhases() {
    const configuration = this.tryGetOrchestrationJson();
    const flows = Array.isArray(configuration?.flows)
      ? configuration.flows
      : [];
    const endpoints = Array.isArray(configuration?.endpoints)
      ? configuration.endpoints
      : [];
    const authProfiles = this.normalizeAuthProfiles(
      configuration?.authProfiles,
    );
    const keysByPhase: Record<OnboardingPhase, Set<string>> = {
      orchestration: new Set<string>(),
      auth: new Set<string>(),
      label: new Set<string>(),
      tracking: new Set<string>(),
      pudo: new Set<string>(),
    };
    const endpointByName = new Map<string, any>();
    for (const endpoint of endpoints) {
      const endpointName =
        typeof endpoint?.name === 'string'
          ? endpoint.name.trim().toLowerCase()
          : '';
      if (endpointName) {
        endpointByName.set(endpointName, endpoint);
      }
    }

    this.addAuthMappingKeys(keysByPhase.auth, configuration?.auth);

    for (const flow of flows) {
      const phase = this.resolvePhaseFromFlow(flow);
      if (phase === 'orchestration') {
        continue;
      }

      const steps = Array.isArray(flow?.steps) ? flow.steps : [];
      for (const step of steps) {
        const mappingKey = step?.options?.mappingFile;
        if (typeof mappingKey === 'string' && mappingKey.trim().length > 0) {
          keysByPhase[phase].add(mappingKey.trim());
        }

        const authMappingKeys = this.resolveAuthMappingKeys(
          step,
          endpointByName,
          authProfiles,
        );
        for (const authMappingKey of authMappingKeys) {
          keysByPhase.auth.add(authMappingKey);
        }
      }
    }

    this.phaseMappingKeys = {
      orchestration: [],
      auth: Array.from(keysByPhase.auth),
      label: Array.from(keysByPhase.label),
      tracking: Array.from(keysByPhase.tracking),
      pudo: Array.from(keysByPhase.pudo),
    };

    this.enabledPhases = {
      orchestration: true,
      auth: this.phaseMappingKeys.auth.length > 0,
      label: this.phaseMappingKeys.label.length > 0,
      tracking: this.phaseMappingKeys.tracking.length > 0,
      pudo: this.phaseMappingKeys.pudo.length > 0,
    };

    if (!this.enabledPhases[this.selectedPhase]) {
      this.selectedPhase = 'orchestration';
    }

    if (this.selectedPhase !== 'orchestration') {
      const availableKeys = this.getCurrentPhaseMappingKeys();
      const selectedKey = this.mappingForm.controls.mappingKey.value;
      if (!availableKeys.includes(selectedKey)) {
        this.mappingForm.controls.mappingKey.setValue(availableKeys[0] ?? '');
      }
    }
  }

  private resolveAuthMappingKeys(
    step: any,
    endpointByName: Map<string, any>,
    authProfiles: Map<string, any>,
  ): string[] {
    const discovered = new Set<string>();
    this.addAuthMappingKeys(discovered, step?.options);

    const stepAuthProfileRef = this.getAuthProfileRefFromNode(step?.options);
    if (stepAuthProfileRef) {
      const profile = authProfiles.get(stepAuthProfileRef.toLowerCase());
      this.addAuthMappingKeys(discovered, profile);
    }

    const endpointName =
      typeof step?.uses === 'string' ? step.uses.trim().toLowerCase() : '';
    if (!endpointName) {
      return Array.from(discovered);
    }

    const endpoint = endpointByName.get(endpointName);
    if (!endpoint) {
      return Array.from(discovered);
    }

    this.addAuthMappingKeys(discovered, endpoint);

    const endpointAuthProfileRef = this.getAuthProfileRefFromNode(endpoint);
    if (!endpointAuthProfileRef) {
      return Array.from(discovered);
    }

    const profile = authProfiles.get(endpointAuthProfileRef.toLowerCase());
    this.addAuthMappingKeys(discovered, profile);
    return Array.from(discovered);
  }

  private normalizeAuthProfiles(rawAuthProfiles: any): Map<string, any> {
    const result = new Map<string, any>();
    if (!rawAuthProfiles) {
      return result;
    }

    // Object model: { "labelAuth": { ... }, "trackingAuth": { ... } }
    if (
      typeof rawAuthProfiles === 'object' &&
      !Array.isArray(rawAuthProfiles)
    ) {
      for (const [key, value] of Object.entries(rawAuthProfiles)) {
        const name = key.trim().toLowerCase();
        if (name) {
          result.set(name, value);
        }
      }
      return result;
    }

    // Array model: [{ name: "labelAuth", ... }, ...]
    if (Array.isArray(rawAuthProfiles)) {
      for (const item of rawAuthProfiles) {
        const name =
          typeof item?.name === 'string' ? item.name.trim().toLowerCase() : '';
        if (name) {
          result.set(name, item);
        }
      }
    }

    return result;
  }

  private addAuthMappingKeys(target: Set<string>, node: any): void {
    const keys = this.getAuthMappingFilesFromNode(node);
    for (const key of keys) {
      target.add(key);
    }
  }

  private getAuthMappingFilesFromNode(node: any): string[] {
    if (!node || typeof node !== 'object') {
      return [];
    }

    const candidates = [
      node.requestMappingFile,
      node.authRequestMappingFile,
      node.responseMappingFile,
      node.authResponseMappingFile,
      node.auth?.requestMappingFile,
      node.auth?.authRequestMappingFile,
      node.auth?.responseMappingFile,
      node.auth?.authResponseMappingFile,
    ];

    const result: string[] = [];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        result.push(candidate.trim());
      }
    }

    return result;
  }

  private getAuthProfileRefFromNode(node: any): string | null {
    if (!node || typeof node !== 'object') {
      return null;
    }

    const candidates = [
      node.authProfileRef,
      node.authProfile,
      node.auth?.profileRef,
      node.auth?.authProfileRef,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }

    return null;
  }

  private tryGetOrchestrationJson(): any {
    try {
      return JSON.parse(this.editorForm.controls.configurationJson.value);
    } catch {
      return null;
    }
  }

  private resolvePhaseFromFlow(flow: any): OnboardingPhase {
    const descriptor =
      `${flow?.trigger ?? ''}|${flow?.name ?? ''}`.toLowerCase();
    if (descriptor.includes('tracking')) {
      return 'tracking';
    }

    if (descriptor.includes('label')) {
      return 'label';
    }

    if (descriptor.includes('pudo')) {
      return 'pudo';
    }

    if (descriptor.includes('manifest')) {
      return 'label';
    }

    return 'orchestration';
  }

  private isPhaseComplete(phase: OnboardingPhase): boolean {
    const keys = this.phaseMappingKeys[phase] ?? [];
    if (keys.length === 0) {
      return false;
    }

    return keys.every((key) => this.hasSavedMappingJson(key));
  }

  private hasSavedMappingJson(mappingKey: string): boolean {
    const existing = this.existingMappings.find(
      (x) => x.mappingKey.toLowerCase() === mappingKey.toLowerCase(),
    );

    if (!existing) {
      return false;
    }

    const json = (existing.mappingJson ?? '').trim();
    return json.length > 0 && json !== '{}' && json !== 'null';
  }

  private openConfigurationDrawer(
    row: CarrierConfigurationListItemDto,
    readOnly: boolean,
  ) {
    this.isEditMode = true;
    this.isReadOnlyMode = readOnly;
    this.currentRow = row;
    this.editorDetailLoading = true;
    this.toggleEditorDrawer(true);
    this.loadConfigurationById(row.id, readOnly);
  }

  private loadConfigurationById(configurationId: number, readOnly: boolean) {
    this.editorDetailLoading = true;
    this.service.getById(configurationId).subscribe({
      next: (data) => {
        this.selectedConfiguration = data;
        this.editorForm.controls.carrierCode.setValue(data.carrierCode);
        this.editorForm.controls.isActive.setValue(
          data.isActive ? 'active' : 'inactive',
        );
        this.editorForm.controls.configurationJson.setValue(
          JSON.stringify(data.configuration, null, 2),
        );
        this.lastSavedConfigurationJson =
          this.editorForm.controls.configurationJson.value;
        this.currentConfigurationId = data.id;
        this.selectedEditorVersionId = data.id;
        this.isReadOnlyMode = readOnly;
        const prev = this.currentRow;
        this.currentRow = {
          id: data.id,
          carrierCode: data.carrierCode,
          activeVersion:
            prev?.activeVersion ?? (data.isActive ? data.version : null),
          latestVersion: prev?.latestVersion ?? data.version,
          updatedAtUtc: prev?.updatedAtUtc ?? data.createdAtUtc,
        };
        this.selectedPhase = 'orchestration';
        this.mappingForm.controls.mappingKey.setValue('');
        this.mappingForm.controls.mappingJson.setValue('{}');
        this.resetPhaseDrafts();
        this.recalculateEnabledPhases();
        this.loadEditorVersions(data.carrierCode, data.id);
        this.loadMappings(data.id);
        this.editorDetailLoading = false;
      },
      error: () => {
        this.editorDetailLoading = false;
        this.storeService.triggerNotification(
          this.i18nService.translate('carrier_config_load_error'),
          'error',
          'exclamation-octagon',
        );
      },
    });
  }

  /**
   * Version number for the list row's open id (active version when set, otherwise latest).
   * Used when the delete modal was opened from the table (list row only).
   */
  getVersionNumberForListRow(row: CarrierConfigurationListItemDto): number {
    if (row.activeVersion != null) {
      return row.activeVersion;
    }
    return row.latestVersion;
  }

  hasUnsavedPhaseDrafts(): boolean {
    if (this.selectedPhase === 'orchestration') {
      return false;
    }

    return this.getDirtyPhaseDrafts().length > 0;
  }

  getTestDurationText(): string {
    if (this.testDurationMs === undefined) {
      return 'n/a';
    }

    return `${Math.round(this.testDurationMs)} ms`;
  }

  private getConfigurationPatchForPhaseSave():
    | CarrierConfiguration
    | null
    | undefined {
    const parsed = this.parseConfigurationJson(
      this.editorForm.controls.configurationJson.value,
    );
    if (!parsed) {
      this.storeService.triggerSidePanelNotification(
        this.i18nService.translate('carrier_config_invalid_json'),
        'configurationJson',
        'error',
        'exclamation-octagon',
      );
      return undefined;
    }

    const saved = this.parseConfigurationJson(this.lastSavedConfigurationJson);
    if (saved && saved.normalized === parsed.normalized) {
      return null;
    }

    return parsed.parsed;
  }

  private parseConfigurationJson(
    value: string,
  ): { normalized: string; parsed: CarrierConfiguration } | null {
    try {
      const parsed = JSON.parse(value) as CarrierConfiguration;
      return { normalized: JSON.stringify(parsed), parsed };
    } catch {
      return null;
    }
  }

  private isMeaningfulJson(value: string): boolean {
    const trimmed = value.trim();
    return (
      trimmed.length > 0 &&
      trimmed !== '{}' &&
      trimmed !== '[]' &&
      trimmed !== 'null'
    );
  }

  private extractErrorMessage(error: any): string | null {
    if (!error) {
      return null;
    }

    const payload = error?.error;
    if (typeof payload === 'string' && payload.trim().length > 0) {
      return payload;
    }

    if (
      typeof payload?.message === 'string' &&
      payload.message.trim().length > 0
    ) {
      return payload.message;
    }

    if (typeof error?.message === 'string' && error.message.trim().length > 0) {
      return error.message;
    }

    return null;
  }

  private requestSaveConfirmation(action: () => void) {
    this.pendingSaveAction = action;
    this.confirmModalMode = 'save';
    this.saveDescriptionControl.setValue('');
    this.toggleConfirmModal(true);
  }

  onSaveDescriptionInput(event: Event) {
    const value = (event.target as HTMLTextAreaElement | null)?.value ?? '';
    this.saveDescriptionControl.setValue(value);
  }

  isSaveConfirmationDisabled(): boolean {
    return this.confirmModalMode === 'save' && !this.isSaveDescriptionValid();
  }

  private isSaveDescriptionValid(): boolean {
    const value = this.saveDescriptionControl.value.trim();
    return value.length > 0 && value.length <= this.saveDescriptionMaxLength;
  }

  private getSaveDescription(): string | null {
    const value = this.saveDescriptionControl.value.trim();
    if (value.length === 0 || value.length > this.saveDescriptionMaxLength) {
      return null;
    }

    return value;
  }

  private openStandaloneTestPanel(
    carrierCode: string,
    configurationId?: number,
  ) {
    if (!carrierCode || !configurationId) {
      return;
    }

    this.testCarrierCode = carrierCode;
    this.testFlowName = 'label-buy';
    this.testRequestJson = '{}';
    this.testResponseJson = '';
    this.testResponseStatus = '';
    this.testDurationMs = undefined;
    this.testInProgress = false;
    this.selectedTestConfigurationId = configurationId;
    this.loadTestVersions(carrierCode, configurationId);
    this.toggleTestDrawer(true);
  }

  private loadTestVersions(
    carrierCode: string,
    preferredConfigurationId?: number,
  ) {
    this.service.listVersionsByCarrier(carrierCode).subscribe({
      next: (items) => {
        this.testVersions = items;
        const preferred =
          preferredConfigurationId ?? this.selectedTestConfigurationId;
        const exists = preferred
          ? items.some((x) => x.id === preferred)
          : false;
        this.selectedTestConfigurationId = exists
          ? preferred
          : (items[0]?.id ?? undefined);
      },
      error: () => {
        this.testVersions = [];
        this.selectedTestConfigurationId = preferredConfigurationId;
      },
    });
  }

  private loadEditorVersions(
    carrierCode: string,
    selectedConfigurationId?: number,
  ) {
    this.service.listVersionsByCarrier(carrierCode).subscribe({
      next: (items) => {
        this.editorVersions = items;
        const selected = selectedConfigurationId ?? this.currentConfigurationId;
        this.selectedEditorVersionId =
          selected && items.some((x) => x.id === selected)
            ? selected
            : (items[0]?.id ?? undefined);
      },
      error: () => {
        this.editorVersions = [];
      },
    });
  }

  private activateCurrentVersion(configurationId: number) {
    this.service.activate(configurationId).subscribe({
      next: () => {
        this.editorForm.controls.isActive.setValue('active');
        this.loadConfigurations();
        if (this.currentRow?.carrierCode) {
          this.loadEditorVersions(this.currentRow.carrierCode, configurationId);
        }
      },
      error: (err) => {
        const message = this.extractErrorMessage(err);
        this.storeService.triggerSidePanelNotification(
          message ??
            this.i18nService.translate('carrier_config_activate_error'),
          '',
          'error',
          'exclamation-octagon',
        );
      },
    });
  }

  private persistCurrentMappingDraft() {
    if (this.selectedPhase === 'orchestration' || this.isReadOnlyMode) {
      return;
    }

    const key = this.mappingForm.controls.mappingKey.value.trim();
    if (!key) {
      return;
    }

    this.phaseDrafts[this.selectedPhase][key.toLowerCase()] =
      this.mappingForm.controls.mappingJson.value;
  }

  private getDraftValue(
    phase: OnboardingPhase,
    mappingKey: string,
  ): string | null {
    if (phase === 'orchestration') {
      return null;
    }

    const value = this.phaseDrafts[phase][mappingKey.toLowerCase()];
    return value === undefined ? null : value;
  }

  private getDirtyPhaseDrafts(): { mappingKey: string; mappingJson: string }[] {
    if (this.selectedPhase === 'orchestration') {
      return [];
    }

    const keys = this.getCurrentPhaseMappingKeys();
    const draftMap = this.phaseDrafts[this.selectedPhase];
    const dirty: { mappingKey: string; mappingJson: string }[] = [];
    for (const key of keys) {
      const draftValue = draftMap[key.toLowerCase()];
      if (draftValue === undefined) {
        continue;
      }

      const existing = this.existingMappings.find(
        (x) => x.mappingKey.toLowerCase() === key.toLowerCase(),
      )?.mappingJson;
      if ((existing ?? '{}') !== draftValue) {
        dirty.push({
          mappingKey: key,
          mappingJson: draftValue,
        });
      }
    }

    return dirty;
  }

  private resetPhaseDraftFor(phase: OnboardingPhase) {
    if (phase === 'orchestration') {
      return;
    }

    this.phaseDrafts[phase] = {};
  }

  private resetPhaseDrafts() {
    this.phaseDrafts = {
      orchestration: {},
      auth: {},
      label: {},
      tracking: {},
      pudo: {},
    };
  }
}
